/**
 * EditOpportunityModal.jsx - the record editor: create or edit one
 * opportunity OR one project.
 *
 * An opportunity and a project are the same record (Open / Lost / Cancelled /
 * No Quote is an opportunity, Won / Complete a project), and since 10/8/26
 * (Sharif: "for Projects, Edit pops up a box") this is the one way to edit
 * either: open it and the Summary's fields are editable at once, then Save.
 * The Projects tab opens a project here as a full page (`asPage`), in place
 * of the read-only project page and its Edit pop-up (both retired); its
 * "+ New Project" opens it with `kind="project"` (status Won). Which fields
 * show follows the Status in the form (editOpportunityForm OPPORTUNITY_ONLY /
 * PROJECT_ONLY); a hidden field keeps its value. A project adds what its page
 * had: the quoted band and the actuals (FinancePanel - on the GLOBAL KSA tab
 * the costs and profit panel), Active / Closed, the Invoicing fields with
 * "Add an invoice", Budget / Time & cost / Expenses / Equipment & Materials /
 * COGS (main tab), and its numbers' linked files.
 * On the main tab the Invoicing fields are read only, worked out from the
 * invoice ledger, and "Add an invoice" writes a ledger row at once
 * (ProjectInvoicesBox, 10/11/26); the GLOBAL KSA tab still types them.
 * Topic and Name are one field, "Name" (the server keeps topic = name).
 *
 * Rights (data/roles): a record opened as a project - or "+ New Project" - is
 * edited and deleted with Projects Edit, an opportunity with Opportunities
 * Edit, whichever tab it was opened from. Without them every field is read
 * only and there is no Save.
 *
 * Also the CRM's "+ New Opportunity" (it seeds customer - and accountId, the
 * exact site - from the account). Re-exported from views/OpportunitiesView
 * so existing import paths keep working.
 *
 * Its tasks live on the Tasks tab (T-01: one task list - the separate
 * "Action items" block that sat under the Summary is gone). The Activity tab
 * (AL-01) is its timeline - OpportunityActivityTab, never on the KSA tab.
 *
 * T-03 (Paul's 1.2 retest): a NEW opportunity on the main tab needs its first
 * task (the Summary tab's "First task" section), like a new account or site.
 * It is checked here and POSTed with the record as first_task, and the server
 * saves both in one transaction. The KSA tab has no such rule, and nor does a
 * new project (won work: its status can only be Won or Complete).
 *
 * Q-02: when a save changes an opportunity's Status to Won (a new record
 * saved as Won too), the Won project setup (PO form) opens before the editor
 * closes, as it does from every other route to Won. That includes a new
 * record whose staged Doc Hub files did not all upload: the PO form opens and
 * says which files to add again, instead of the editor stopping on the upload
 * error. A project (already Won / Complete, or made by "+ New Project") is
 * not asked: its PO fields are on its own Summary.
 *
 * r50 (Sharif, 10/2/26 - two people on one opportunity): a save of an
 * existing record sends only the fields changed here, guarded by the values
 * they had when the editor opened (saveProject + diffForSave); a new record's
 * quote number comes from the server; the server's refusals (quote already
 * used, someone else changed a field) are answered in ConflictDialog without
 * losing anything typed; and a live change to this record by someone else
 * shows a banner while the editor is open.
 *
 * Main project / sub-project / PO line (10/14/26, data/projectHierarchy):
 * the links are set by hand (projects.parent_id). On the main tab a saved
 * record has a "Sub-projects" tab: its Children (Remove each, "Add existing
 * projects..." to put records under it - ChildrenSection) and, when it is in
 * a family, the whole family with the roll-up figures and the Tracking
 * roll-up. Its name shows where it sits; a project offers "Add PO line" /
 * "Add sub-project" (made from it, put under it); the Summary has the "Main
 * project" picker; a main record's Tracking tab can show every record under
 * it added up.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Toast } from '../ui/Toast';
import { Icon } from '../ui/Icon';
import CommentsPanel from '../shared/CommentsPanel';
import { contactsForHome, indexById, siteOptions } from '../crm/nextStep';
import { taskPayload } from '../crm/crmFormHelpers';
import { localDay } from '../tasks/tasksTable';
import WonProjectSetup from './WonProjectSetup';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';
import { useApp, toSnake } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { canEdit, canDelete, hasArea } from '../../data/roles';
import { isProjectRow } from '../../data/projectStatus';
import { useMyAccess } from '../../hooks/useMyAccess';
import { useRefs } from '../../hooks/useRefs';
import { useApiBase, useDataScope } from '../../context/DataScope';
import {
  buildInitialForm,
  buildSavePayload,
  deriveQuoteMath,
  isProjectForm,
  OPP_FIELD_LABELS,
  fetchNextQuote,
  loadedValues,
} from './editOpportunityForm';
import SaveGuardDialog, { useQuotePrefill, useRecordChangeNote, useSaveGuard } from './ConflictDialog';
import { createRenderRow } from './EditOpportunityFields';
import QuoteBuilder from '../quotes/QuoteBuilder';
import { childrenOf, familyOf } from '../../data/projectHierarchy';
import AddChildDialog from '../projects/hierarchy/AddChildDialog';
import { blank } from './editOpportunity/editorBits';
import { useRecordDocs } from './editOpportunity/useRecordDocs';
import { makeSaveForm } from './editOpportunity/saveForm';
import { makeContent } from './editOpportunity/editorContent';
import { makeHeaderValues } from './editOpportunity/headerValues';

/**
 * Props:
 *   opp        the record; without an `id` the editor creates one (a seed such
 *              as { customer, status, accountId } prefills it)
 *   onClose    leave the editor (in a pop-up: close it; on a page: go back)
 *   onSaved    after every good save
 *   kind       'project' for "+ New Project": status Won (Won / Complete
 *              only), no first task, no PO form after the save
 *   asPage     render as a full page (the Projects tab's project page)
 *              instead of a pop-up; a save keeps it open on the saved values
 *   onDeleted  after the record is deleted (onClose follows)
 *   backLabel  the page's way back ("Back to Projects")
 *   initialTab the tab it opens on ('family' - a main project opened from its group row)
 *   onOpenRecord(id, tab)
 *              open another record of the family (the Projects tab);
 *              without it a link of the hash does it (#/projects?project=)
 */
// The pop-up window (Sharif, 10/20/26: "make the window a little bigger and fit
// everything in that window"): as wide and tall as the screen allows, the footer
// fixed, and ONE scroll area above it holding the hero header and the body. The
// header is sticky with a negative top (heroTop): scrolling slides its title,
// cards and stages away and the tab bar stays pinned, so a tall header (a
// project's actuals band) never squeezes the form on a laptop screen.
// POPUP_GUTTER: 60 px each side, so the chat button (fixed, bottom-right) never
// sits on top of Save on a laptop screen.
const POPUP_MAX_W = 1500;
const POPUP_GUTTER = 120;
const POPUP_STYLE = {
  maxWidth: `min(${POPUP_MAX_W}px, calc(100vw - ${POPUP_GUTTER}px))`,
  width: '100%',
  height: 'min(94vh, calc(100vh - 48px))',
  maxHeight: 'none',
};

export function EditOpportunityModal({
  opp,
  onClose,
  onSaved,
  kind,
  asPage = false,
  onDeleted,
  backLabel = 'Back to Projects',
  initialTab,
  onOpenRecord,
}) {
  // `opp` without an `id` triggers create mode (createProject instead of saveProject).
  // Used by CRM's "+ New Opportunity" button, which seeds `customer` from the account.
  // updateProject is the Won PO form's save (it sends only the PO fields).
  // refreshQuietly / applySavedProject are the main tab's (AppContext); the
  // GLOBAL KSA tab's value has neither and re-reads with fetchAll as before.
  const {
    updateProject,
    createProject,
    saveProject,
    deleteOpportunity,
    deleteProject,
    allAccounts,
    allProjects,
    users,
    resolveOwnerName,
    fetchAll,
    refreshQuietly,
    applySavedProject,
  } = useApp();
  const { user } = useAuth();
  const access = useMyAccess();
  const isCreate = !opp || !opp.id;
  // A project as opened (Won / Complete), or "+ New Project": the Projects
  // area's rights; an opportunity: the Opportunities area's.
  const openedAsProject = isCreate ? kind === 'project' : isProjectRow(opp);
  const area = openedAsProject ? 'projects' : 'opportunities';
  const editable = canEdit(access, area);
  const deletable = canDelete(access, area);
  const { refs, addRef, removeRef, mayRemove } = useRefs();
  // On the GLOBAL KSA tab every call goes to the KSA endpoints (its own
  // comments, tasks and Doc Hub).
  const base = useApiBase();
  // ...and has no CRM accounts, so no Site / account link there (A-02)
  const scope = useDataScope();
  // Canonical dropdown lists keyed by refKind (see renderRow kind 'ref').
  const refLists = {
    customer: refs.customers,
    engineer: refs.engineers.map((e) => e.name),
    approval_manager: refs.approvalManagers,
    project_manager: refs.projectManagers,
  };
  // Set once a create has gone through. If a staged file then fails to upload
  // the editor stays open with the error, and pressing Save again must update
  // that record - it used to create a second copy of it.
  const [createdId, setCreatedId] = useState(null);
  const [tab, setTab] = useState(initialTab || 'summary');
  // a KSA record's PO is in SAR, so a blank currency starts as SAR there (r44)
  const [f, setF] = useState(() => buildInitialForm(opp, isCreate, user, scope ? { defaultCurrency: 'SAR' } : undefined));
  // the stage the form is at now (its Status): which fields, tabs and bands show
  const isProject = isProjectForm(f);
  const noun = isProject ? 'Project' : 'Opportunity';
  // Files picked in the Doc Hub before the record exists. Held here, uploaded
  // in handleSave once the POST hands back an id.
  const [stagedDocs, setStagedDocs] = useState([]);
  const [toast, setToast] = useState('');
  const [saveError, setSaveError] = useState('');
  // the required fields the last Save found blank: their rows show red until filled
  const [missing, setMissing] = useState({});
  const [scrollTo, setScrollTo] = useState(null);
  const bodyRef = useRef(null);
  // the pop-up's sticky header stops with only its tab bar showing ([data-hero-tabs])
  const heroRef = useRef(null);
  const [heroTop, setHeroTop] = useState(0);
  useEffect(() => {
    const hero = heroRef.current;
    if (asPage || !hero) return undefined;
    const calc = () => {
      const tabsEl = hero.querySelector('[data-hero-tabs]');
      if (tabsEl) setHeroTop(-tabsEl.offsetTop); // offsetTop: relative to the (sticky) hero, never scaled by the pop-in
    };
    calc();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(calc);
    ro.observe(hero);
    return () => ro.disconnect();
  }, [asPage]);
  // Q-02: the status as last saved, so a save that makes it Won asks for the PO
  // once. The next-step prompt's "close as Won" asks itself and updates this.
  const savedStatus = useRef(isCreate ? '' : opp.status || '');
  const [wonSetup, setWonSetup] = useState(null); // the record just saved as Won
  // T-03: the first task of a new opportunity (main tab only). Owner defaults
  // to me, due date to today (local), as on a new account or site.
  const needsFirstTask = isCreate && !scope && !openedAsProject;
  const [firstTask, setFirstTask] = useState(() => ({
    description: '',
    owner: (user && user.name) || '',
    due_date: localDay(),
    contact_id: '',
    tagged: [],
  }));
  const [taskErrs, setTaskErrs] = useState({});
  const setTask = (k, v) => {
    setFirstTask((p) => ({ ...p, [k]: v }));
    setTaskErrs((p) => {
      if (!p[k]) return p;
      const n = { ...p };
      delete n[k];
      return n;
    });
    setSaveError('');
  };
  const { recordId, loadCommentCount, docHubCount, docs, setCommentCount, loadDocs, setShowComments, commentCount, showComments } =
    useRecordDocs({ createdId, opp, isCreate, base });
  // New opportunities get the next free quote number prefilled (still editable)
  // — hand-picked numbers are where the duplicate quote letters came from.
  // r50: the server's number (main + KSA are one series), offered again when
  // someone else's new record arrives; a typed number is never replaced.
  const offeredQuote = useQuotePrefill({
    enabled: isCreate && !createdId,
    base,
    allProjects,
    quote: f.quoteLetter,
    setQuote: (v, cur) => setF((p) => (p.quoteLetter === cur ? { ...p, quoteLetter: v } : p)),
  });

  // r50: the record as the editor opened it - the payload a save would build
  // (snap) and the record's own values (loaded). A save of an existing record
  // sends only the fields whose value is no longer the one in snap, each
  // expecting its value in loaded.
  const snap = useRef(null);
  const loaded = useRef(null);
  if (snap.current === null) {
    snap.current = buildSavePayload(f, deriveQuoteMath(f), { linkSite: !scope, ledger: !scope });
    loaded.current = isCreate ? {} : loadedValues(opp, Object.keys(snap.current));
  }
  // Values now on the server (saved here, saved by the Tasks tab's PO form,
  // or taken with "Use theirs"): the next save neither resends them nor
  // expects the old ones. `form` is the form holding them.
  const rebase = (form, values) => {
    const next = buildSavePayload(form, deriveQuoteMath(form), { linkSite: !scope, ledger: !scope });
    const s = { ...snap.current };
    const l = { ...loaded.current };
    for (const [k, v] of Object.entries(values || {})) {
      if (!(k in next)) continue;
      s[k] = next[k];
      l[k] = v;
    }
    // the leftover is worked out from the PO value and the invoiced amount:
    // settled with them, so it is never sent as an edit of its own
    if (values && ('poValue' in values || 'invoicedAlready' in values)) s.moneyLeftover = next.moneyLeftover;
    snap.current = s;
    loaded.current = l;
  };
  const { ask, asking, answer } = useSaveGuard();
  // "Add an invoice" saved a ledger row: the read-only invoicing fields show
  // what the server worked out (never sent back - LEDGER_FIELDS), and the
  // lists behind the editor re-read (this tab's own write is not echoed live)
  const invoiceAdded = (p) => {
    const text = (v) => (v === null || v === undefined ? '' : String(v));
    setF((cur) => ({
      ...cur,
      invoicedAlready: text(p.invoicedAlready),
      invoiceNumbers: text(p.invoiceNumbers),
      latestInvoiceDate: text(p.latestInvoiceDate),
    }));
    if (refreshQuietly) refreshQuietly(['projects']);
    else if (fetchAll) fetchAll();
  };
  // r50: someone else's save of this record while it is open (live events)
  const [liveNote, dismissLiveNote] = useRecordChangeNote(recordId, base, OPP_FIELD_LABELS);
  // Styled delete confirmation (same dialog + server-rules warning as Projects).
  const [confirmDelete, setConfirmDelete] = useState(false);
  // r53: Build quote (Quote Master in GlobalOps) - Quotes & Rates View. On the
  // GLOBAL KSA tab too (10/7/26): the builder takes the tab's API base, so a
  // KSA record is priced on KSA's own rate card (SAR) and its draft kept on it.
  const [quoteOpen, setQuoteOpen] = useState(false);
  // main project / sub-project / PO line: where this record sits (main tab, a saved record)
  const family = useMemo(
    () => (!scope && !isCreate && opp && opp.id ? familyOf(allProjects || [], opp.id) : null),
    [scope, isCreate, opp, allProjects]
  );
  const [addKind, setAddKind] = useState(null); // 'line' | 'sub' - AddChildDialog
  const [trackRollup, setTrackRollup] = useState(false); // a main record's Tracking: this record or all under it
  const openRecord = (id, toTab) => {
    if (onOpenRecord) {
      onOpenRecord(id, toTab);
      return;
    }
    window.location.hash = `#/projects?project=${encodeURIComponent(id)}${toTab ? `&tab=${toTab}` : ''}`;
  };
  // the crumb's links: the main project's record, or the sub-project's, on its Sub-projects tab
  const openFamily = (which) => {
    if (!family) return;
    openRecord(String(which === 'sub' && family.sub ? family.sub.head.id : family.main.root.id), 'family');
  };
  // the Sub-projects tab: every saved main-tab record has one (its Children, to link by hand)
  const childCount = !scope && !isCreate && opp && opp.id ? childrenOf(allProjects || [], opp.id).length : 0;
  const familyLabel = !scope && !isCreate ? `Sub-projects${childCount ? ` (${childCount})` : ''}` : null;
  const canQuote = !isCreate && hasArea(access, 'quotes', 'view') && (!scope || hasArea(access, 'ksa', 'view'));
  useEffect(() => {
    loadCommentCount(!asPage);
  }, [loadCommentCount, asPage]);

  // Dock the floating post-it to the MODAL's right edge (the modal is centered,
  // width min(1500, viewport - 120) - POPUP_STYLE), or to the window's on a page. Clamp so it
  // can't run off-screen on narrower monitors. Recomputed on resize.
  const POSTIT_W = 290;
  const [dockLeft, setDockLeft] = useState(0);
  useEffect(() => {
    const calc = () => {
      const modalW = Math.min(POPUP_MAX_W, window.innerWidth - POPUP_GUTTER);
      const modalRight = (window.innerWidth + modalW) / 2; // right edge of the centered modal
      const left = asPage ? window.innerWidth - POSTIT_W - 16 : Math.min(modalRight - 6, window.innerWidth - POSTIT_W - 8); // hug the edge, keep on-screen
      setDockLeft(Math.max(8, left));
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [asPage]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  // The Customer field's flush(), for handleSave (r49 fix F6).
  const customerField = useRef(null);

  // A-02: link the exact site. Picking one on a blank Customer fills it the
  // way the Leads board did (the site's name key), so the row still shows.
  const sites = useMemo(() => (scope ? [] : siteOptions(allAccounts)), [scope, allAccounts]);
  const accountsById = useMemo(() => (scope ? new Map() : indexById(allAccounts)), [scope, allAccounts]);
  const setSite = (id) =>
    setF((p) => {
      const a = id ? accountsById.get(String(id)) : null;
      const seed = a ? a.opportunityKey || a.opportunity_key || a.name || '' : '';
      return { ...p, accountId: id, customer: String(p.customer || '').trim() ? p.customer : seed };
    });
  const siteLink = scope ? null : { options: sites, value: f.accountId || '', onChange: setSite };
  // the first task may name one of the linked site's contacts (or its company's)
  const taskContacts = useMemo(
    () => (needsFirstTask && f.accountId ? contactsForHome('account', f.accountId, accountsById) : []),
    [needsFirstTask, f.accountId, accountsById]
  );
  const taskOwners = Array.from(
    new Set([...(users || []).map((u) => u.name).filter(Boolean), ...(firstTask.owner ? [firstTask.owner] : [])])
  ).sort((a, b) => a.localeCompare(b));
  const firstTaskProps =
    needsFirstTask && !createdId
      ? { value: firstTask, onChange: setTask, errors: taskErrs, owners: taskOwners, contacts: taskContacts }
      : null;

  // A Save stopped on a blank required field: show the Summary and bring that
  // field into view (it may be far down the page or the pop-up's scroll box).
  useEffect(() => {
    if (!scrollTo || tab !== 'summary') return;
    const row = bodyRef.current && bodyRef.current.querySelector(`[data-field="${scrollTo}"]`);
    if (row) {
      if (row.scrollIntoView) row.scrollIntoView({ block: 'center' });
      const box = row.querySelector('input, select, textarea');
      if (box) box.focus();
    }
    setScrollTo(null);
  }, [scrollTo, tab]);

  // T-03: create the opportunity and its first task in one request. Not
  // createProject: it has no first task. Answers like createProject does
  // ({ ok, status, body }), so a 409 (quote already used) is asked about the
  // same way. The URL comes from useApiBase (server/test_ksa.js).
  const createWithFirstTask = async (payload, { allowDuplicateQuote } = {}) => {
    const contactOk = firstTask.contact_id && taskContacts.some((c) => String(c.id) === String(firstTask.contact_id));
    const firstTaskBody = { ...taskPayload(firstTask), ...(contactOk ? { contact_id: firstTask.contact_id } : {}) };
    try {
      const r = await fetch(`${base}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...toSnake(payload),
          first_task: firstTaskBody,
          ...(allowDuplicateQuote ? { allow_duplicate_quote: true } : {}),
        }),
      });
      const body = await r.json().catch(() => null);
      if (r.ok && body) {
        // the new row is in the lists at once; they re-read behind it
        if (applySavedProject && refreshQuietly) {
          applySavedProject(body);
          refreshQuietly(['projects', 'accounts']);
        } else if (typeof fetchAll === 'function') await fetchAll();
      }
      return { ok: r.ok && !!body, status: r.status, body };
    } catch {
      return { ok: false, status: 0, body: null };
    }
  };
  const setNum = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value.replace(/[^0-9.]/g, '') }));

  // Derived (pure math — see deriveQuoteMath above)
  const derived = deriveQuoteMath(f);
  const { price, totalCogs, quotedProfit, grossMarginPct, goPct, getPct, goGetPct, anticipatedValue, marginColor } = derived;
  const ownerName = resolveOwnerName(f.owner);

  // one save at a time: a double click used to be able to create the record twice.
  // `busy` shows it (10/13/26): the button reads "Saving..." and is disabled,
  // where a second click used to be ignored without a word - it looked like
  // the save had not happened. Leaving waits for the save to finish, so an
  // in-flight save is never dropped by Cancel, the X or Back.
  const saving = useRef(false);
  const [busy, setBusy] = useState(false);
  const handleSave = async () => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    try {
      await saveForm();
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };
  const leave = () => {
    if (!saving.current) onClose();
  };
  const saveForm = makeSaveForm({
    f,
    customerField,
    isCreate,
    setMissing,
    setTab,
    setScrollTo,
    setSaveError,
    noun,
    firstTaskProps,
    firstTask,
    setTaskErrs,
    scope,
    offeredQuote,
    setF,
    createdId,
    opp,
    ask,
    createWithFirstTask,
    createProject,
    setCreatedId,
    snap,
    loaded,
    stagedDocs,
    base,
    setStagedDocs,
    rebase,
    saveProject,
    savedStatus,
    openedAsProject,
    setToast,
    onSaved,
    setWonSetup,
    asPage,
    onClose,
  });

  // Same flow for both: the button shows for any deletable role, the styled
  // modal warns, and the SERVER decides (only user-created rows delete;
  // imported rows 403). window.confirm() is gone.
  const handleDelete = () => setConfirmDelete(true);
  const reallyDelete = async () => {
    setConfirmDelete(false);
    // deleteProject reports false when refused (main and KSA); the main
    // deleteOpportunity returns nothing
    const remove = openedAsProject && deleteProject ? deleteProject : deleteOpportunity;
    if ((await remove(opp.id)) === false) {
      setSaveError('Could not delete - try again.');
      return;
    }
    if (onDeleted) onDeleted();
    onClose();
  };

  // Plain render function — see createRenderRow for why this must never
  // become a component type.
  const missingNow = Object.fromEntries(
    Object.keys(missing)
      .filter((k) => blank(f[k]))
      .map((k) => [k, true])
  );
  // "Next #" beside the quote number asks the server too (never a guess from the loaded rows first)
  const nextQuote = async () => {
    const n = await fetchNextQuote(base, allProjects);
    if (n) {
      offeredQuote.current = n;
      setF((p) => ({ ...p, quoteLetter: n }));
    }
  };
  const renderRow = createRenderRow({
    f,
    set,
    setF,
    setNum,
    allAccounts,
    users,
    resolveOwnerName,
    refLists,
    addRef,
    removeRef: mayRemove ? removeRef : null,
    customerField,
    missing: missingNow,
    onNextQuote: editable ? nextQuote : null,
  });

  const {
    shown,
    metaAccount,
    metaEstClose,
    metaEstRevenue,
    projectCards,
    pills,
    fromMatch,
    actuals,
    tabs,
    lockFields,
    ksaCosts,
    ksaCurrency,
  } = makeHeaderValues({
    f,
    scope,
    anticipatedValue,
    price,
    totalCogs,
    quotedProfit,
    grossMarginPct,
    marginColor,
    opp,
    isCreate,
    editable,
    isProject,
    familyLabel,
    access,
    tab,
  });
  // Saved as Won: the editor gives way to the PO form, and closing that (saved
  // or "Fill in later") closes the editor too. The status is already saved.
  if (wonSetup) {
    return (
      <>
        {toast && <Toast message={toast} onDone={() => setToast('')} />}
        <WonProjectSetup
          opp={wonSetup}
          notice={wonSetup.uploadError || ''}
          onClose={onClose}
          onSave={async (patch) => !!(await updateProject(wonSetup.id, patch))}
        />
      </>
    );
  }

  const content = makeContent({
    asPage,
    heroRef,
    heroTop,
    isCreate,
    opp,
    f,
    setF,
    shown,
    setTab,
    ownerName,
    grossMarginPct,
    marginColor,
    metaAccount,
    metaEstClose,
    metaEstRevenue,
    docHubCount,
    isProject,
    projectCards,
    pills,
    fromMatch,
    actuals,
    tabs,
    family,
    openFamily,
    scope,
    editable,
    setAddKind,
    liveNote,
    dismissLiveNote,
    bodyRef,
    lockFields,
    renderRow,
    siteLink,
    firstTaskProps,
    openedAsProject,
    allProjects,
    createdId,
    base,
    access,
    invoiceAdded,
    docs,
    noun,
    savedStatus,
    rebase,
    deletable,
    ksaCosts,
    trackRollup,
    setTrackRollup,
    openRecord,
    user,
    saveProject,
    setToast,
    totalCogs,
    quotedProfit,
    goPct,
    getPct,
    goGetPct,
    anticipatedValue,
    ksaCurrency,
    setCommentCount,
    stagedDocs,
    setStagedDocs,
    loadDocs,
    saveError,
    handleDelete,
    handleSave,
    leave,
    busy,
    backLabel,
    setShowComments,
    commentCount,
    canQuote,
    setQuoteOpen,
  });

  return (
    <>
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
      {asPage ? (
        <div>
          <div
            onClick={leave}
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 16, width: 'fit-content' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.7';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <div style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}>
              <Icon name="chevron" size={16} color="#555" />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>{backLabel}</span>
          </div>
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              border: '1px solid #ECECEC',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              overflow: 'hidden',
            }}
          >
            {content}
          </div>
        </div>
      ) : (
        <Modal title="" onClose={leave} style={POPUP_STYLE} bodyClassName="flex flex-col min-h-0 overflow-hidden">
          {content}
        </Modal>
      )}

      {/* r50: quote already used / someone else changed a field - over the editor, never closing it */}
      <SaveGuardDialog asking={asking} answer={answer} labels={OPP_FIELD_LABELS} />

      {quoteOpen && <QuoteBuilder opp={opp} form={f} onClose={() => setQuoteOpen(false)} />}

      {addKind && (
        <AddChildDialog
          source={opp}
          kind={addKind}
          onClose={() => setAddKind(null)}
          onCreated={(row) =>
            setToast(`${addKind === 'line' ? 'PO line' : 'Sub-project'} added${row.po_number ? `: ${row.po_number}` : ''}`)
          }
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          noun={openedAsProject ? 'project' : 'opportunity'}
          name={f.name || opp.name || opp.description || opp.id}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={reallyDelete}
        />
      )}

      {/* Floating comments post-it — fixed to the right of the viewport, so it
          floats beside the editor without taking any of its space. */}
      {!isCreate && showComments && (
        <div
          style={{
            position: 'fixed',
            top: 96,
            left: dockLeft,
            width: POSTIT_W,
            maxHeight: '74vh',
            overflowY: 'auto',
            zIndex: 1100,
            background: '#FFFDF0',
            border: '1px solid #E6D98C',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.20)',
            padding: '12px 14px',
            transform: 'rotate(-0.5deg)',
            fontFamily: "'Montserrat', sans-serif",
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: '#8A6D00', letterSpacing: 1, textTransform: 'uppercase' }}>
              💬 Comments
            </span>
            <button
              onClick={() => setShowComments(false)}
              aria-label="Hide comments"
              style={{ background: 'none', border: 'none', fontSize: 16, lineHeight: 1, cursor: 'pointer', color: '#8A6D00', padding: 2 }}
            >
              ×
            </button>
          </div>
          <CommentsPanel projectId={opp.id} />
        </div>
      )}
    </>
  );
}
